import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderSlot as _renderSlot, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, mergeProps as _mergeProps, withCtx as _withCtx, createSlots as _createSlots, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-1915ee47"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "cm-anchor__body" }
const _hoisted_2 = { class: "cm-anchor__list-wrapper" }
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "cm-anchor__marker-line" }, null, -1))

import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  PropType,
  ref,
  useSlots,
  watch
} from 'vue'
import AnchorLink from './anchor-link.vue'
import type { AnchorItem } from './types'

// 禁用属性继承，避免 container 和 target 等属性传递到 DOM

export default /*#__PURE__*/_defineComponent({
  ...{
  inheritAttrs: false
},
  __name: 'anchor',
  props: {
  modelValue: {
    type: String,
    default: ''
  },
  items: {
    type: Array as PropType<AnchorItem[]>,
    default: () => []
  },
  offset: {
    type: Number,
    default: 0
  },
  bound: {
    type: Number,
    default: 0
  },
  marker: {
    type: Boolean,
    default: false
  },
  affix: {
    type: Boolean,
    default: false
  },
  offsetTop: {
    type: Number,
    default: 0
  },
  zIndex: {
    type: Number,
    default: 100
  },
  scrollDuration: {
    type: Number,
    default: 400
  },
  scrollBehavior: {
    type: String as PropType<'auto' | 'smooth'>,
    default: 'smooth'
  },
  direction: {
    type: String as PropType<'vertical' | 'horizontal'>,
    default: 'vertical'
  },
  useHash: {
    type: Boolean,
    default: false
  }
},
  emits: ["update:modelValue", "change", "select"],
  setup(__props, { expose: __expose, emit: __emit }) {



const props = __props

const emit = __emit

const activeHref = ref(props.modelValue)
const scrollContainerRef = ref<HTMLElement | Window | null>(null)
const scrollTargetRef = ref<HTMLElement | null>(null)
const isScrolling = ref(false)
const anchorRef = ref<HTMLElement | null>(null)
const listRef = ref<HTMLElement | null>(null)
const markerStyle = ref<Record<string, string>>({})
const slots = useSlots()
const anchorLinkExpose = AnchorLink
const isUpdatingHash = ref(false)
const isUserClick = ref(false)
const customContainer = ref<string | HTMLElement | { value?: HTMLElement } | null>(null)
const customTarget = ref<string | HTMLElement | { value?: HTMLElement } | null>(null)
// 滚动相关的定时器，用于取消之前的滚动操作
const scrollTimers = ref<ReturnType<typeof setTimeout>[]>([])
// 用户点击滚动完成后的保护时间，在此期间阻止 scrollListener 更新 activeHref
const userScrollProtectionTimer = ref<ReturnType<typeof setTimeout> | null>(null)
const isUserScrollProtected = ref(false)

const flattenedItems = computed(() => {
  const list: AnchorItem[] = []
  const travel = (items: AnchorItem[]) => {
    items.forEach((item) => {
      list.push(item)
      if (item.children?.length) {
        travel(item.children)
      }
    })
  }
  travel(props.items)
  return list
})

const anchorClass = computed(() => [
  'cm-anchor',
  props.direction === 'horizontal' ? 'is-horizontal' : 'is-vertical',
  { 'is-affix': props.affix }
])

const anchorStyle = computed(() => {
  if (!props.affix) return undefined
  return {
    position: 'sticky',
    top: `${props.offsetTop}px`,
    'z-index': props.zIndex
  } as Record<string, string|number>
})

const markerVisible = computed(() => props.marker && Boolean(activeHref.value))
const hasItemSlot = computed(() => Boolean(slots.item))
const hasTitleSlot = computed(() => Boolean(slots.title))
const direction = computed(() => props.direction)

watch(
  () => props.modelValue,
  (val) => {
    if (val !== activeHref.value) {
      activeHref.value = val
    }
  }
)

watch(activeHref, (val, oldVal) => {
  if (val !== oldVal) {
    emit('update:modelValue', val)
    emit('change', val)
    updateMarker()
  }
})

const updateHash = (href: string) => {
  if (!props.useHash || isUpdatingHash.value) return
  try {
    isUpdatingHash.value = true
    if (href) {
      window.location.hash = href
    } else {
      // 移除 hash
      const url = new URL(window.location.href)
      url.hash = ''
      window.history.replaceState(null, '', url.toString())
    }
  } catch (e) {
    // 忽略错误
  } finally {
    // 延迟重置标志，避免立即触发 hashchange
    setTimeout(() => {
      isUpdatingHash.value = false
    }, 100)
  }
}

const handleHashChange = () => {
  // 如果是用户点击导致的 hash 更新，不需要处理（因为已经滚动过了）
  if (isUpdatingHash.value || isUserClick.value) {
    isUserClick.value = false
    return
  }
  const hash = window.location.hash.slice(1) // 移除 # 号
  if (hash) {
    const href = `#${hash}`
    // 检查这个 href 是否在 items 中
    const exists = flattenedItems.value.some(item => item.href === href)
    if (exists && href !== activeHref.value) {
      activeHref.value = href
      // 延迟滚动，确保 DOM 已更新
      nextTick(() => {
        scrollToSection(href)
      })
    }
  }
}

const handleLinkSelect = (item: AnchorItem) => {
  emit('select', item)
  if (props.useHash) {
    isUserClick.value = true
    updateHash(item.href)
  }

  // 设置用户点击的 activeHref
  activeHref.value = item.href

  // 清除之前的保护定时器
  if (userScrollProtectionTimer.value) {
    clearTimeout(userScrollProtectionTimer.value)
  }

  // 启用用户滚动保护，防止 scrollListener 在滚动完成后错误更新
  isUserScrollProtected.value = true

  scrollToSection(item.href).then(() => {
    // 滚动完成后，保持用户点击的锚点高亮
    // 设置较长的保护期，确保滚动完全稳定
    // 在保护期内，scrollListener 不会更新 activeHref
    userScrollProtectionTimer.value = setTimeout(() => {
      // 保护期结束后，允许 scrollListener 更新
      // 但此时应该已经稳定了，不会错误更新
      isUserScrollProtected.value = false
      userScrollProtectionTimer.value = null
    }, 1500) // 延长保护期到 1500ms，确保滚动完全稳定且位置计算准确
  })
}

const scrollListener = () => {
  if (isScrolling.value) {
    return
  }
  // 如果处于用户滚动保护期，不更新 activeHref（避免滚动完成后错误更新）
  if (isUserScrollProtected.value) {
    return
  }
  const container = scrollContainerRef.value
  if (!container) return
  const targetWrapper = scrollTargetRef.value
  const baseContainer = targetWrapper || container
  const scrollTop = targetWrapper ? targetWrapper.scrollTop : getScrollTop(container)

  // 计算当前视口顶部位置（考虑 offset）
  const viewportTop = scrollTop + props.offset

  let current = ''
  let bestMatch: { href: string; distance: number } | null = null

  // 找到最接近视口顶部的锚点
  flattenedItems.value.forEach((item) => {
    const target = getAnchorElement(item.href)
    if (!target) return

    const top = getOffsetTop(target, baseContainer)
    const distance = Math.abs(top - viewportTop)

    // 如果锚点位置在视口顶部（考虑 offset 和 bound）之前或相等，则考虑它
    if (top <= viewportTop + props.bound) {
      // 如果还没有找到匹配的，或者这个更接近，则选择它
      if (!bestMatch || distance < bestMatch.distance) {
        bestMatch = { href: item.href, distance }
      }
    }
  })

  // 如果找到了匹配的锚点，使用它
  if (bestMatch) {
    current = bestMatch.href
  } else if (flattenedItems.value.length > 0) {
    // 如果没有找到任何锚点（比如滚动到了最顶部），选择第一个锚点
    current = flattenedItems.value[0].href
  }

  if (current && current !== activeHref.value) {
    activeHref.value = current
    // 如果启用 hash 模式，更新 URL hash
    if (props.useHash && !isUpdatingHash.value) {
      updateHash(current)
    }
  }
}

const getAnchorElement = (href: string) => {
  if (!href) return null
  try {
    return document.querySelector(href) as HTMLElement | null
  } catch {
    return null
  }
}

// 获取指定的滚动容器
const getTargetContainer = (): HTMLElement | Window => {
  // 优先使用自定义 container
  if (customContainer.value) {
    if (typeof customContainer.value === 'string') {
      const el = document.querySelector(customContainer.value) as HTMLElement
      if (el) return el
    } else if (customContainer.value instanceof HTMLElement) {
      return customContainer.value
    } else if (customContainer.value && typeof customContainer.value === 'object' && 'value' in customContainer.value) {
      // 处理 Vue ref 对象
      const el = (customContainer.value as { value?: HTMLElement }).value
      if (el && el instanceof HTMLElement) {
        return el
      }
    }
  }

  // 其次使用自定义 target
  if (customTarget.value) {
    if (typeof customTarget.value === 'string') {
      const el = document.querySelector(customTarget.value) as HTMLElement
      if (el) return el
    } else if (customTarget.value instanceof HTMLElement) {
      return customTarget.value
    } else if (customTarget.value && typeof customTarget.value === 'object' && 'value' in customTarget.value) {
      // 处理 Vue ref 对象
      const el = (customTarget.value as { value?: HTMLElement }).value
      if (el && el instanceof HTMLElement) {
        return el
      }
    }
  }

  return window
}

// 查找最近的滚动容器
const findScrollContainer = (element: HTMLElement | null): HTMLElement | Window => {
  if (!element) return window

  let current: HTMLElement | null = element
  while (current && current !== document.body && current !== document.documentElement) {
    const overflowY = window.getComputedStyle(current).overflowY
    const overflowX = window.getComputedStyle(current).overflowX
    const hasScroll = (overflowY === 'scroll' || overflowY === 'auto') ||
                      (overflowX === 'scroll' || overflowX === 'auto')

    if (hasScroll) {
      // 检查是否真的可以滚动
      const canScroll = current.scrollHeight > current.clientHeight ||
                        current.scrollWidth > current.clientWidth
      if (canScroll) {
        return current
      }
    }
    current = current.parentElement
  }
  return window
}

const getScrollContainer = (): HTMLElement | Window => {
  // 如果指定了容器，直接使用
  const targetContainer = getTargetContainer()
  if (targetContainer !== window) {
    return targetContainer
  }

  // 否则自动查找滚动容器
  // 尝试从第一个 anchor item 查找滚动容器
  if (flattenedItems.value.length > 0) {
    const firstHref = flattenedItems.value[0].href
    const firstElement = getAnchorElement(firstHref)
    if (firstElement) {
      const container = findScrollContainer(firstElement)
      if (container !== window) {
        return container
      }
    }
  }
  return window
}

const getScrollTarget = (): HTMLElement | null => {
  // 返回与滚动容器相同的元素
  const container = getScrollContainer()
  return container === window ? null : (container as HTMLElement)
}

// 暴露给外部的方法：滚动到指定锚点
const scrollTo = (href: string | AnchorItem) => {
  const item = typeof href === 'string'
    ? flattenedItems.value.find(i => i.href === href)
    : href

  if (!item) {
    const hrefStr = typeof href === 'string' ? href : href.href
    console.warn(`Anchor: Cannot find item with href "${hrefStr}"`)
    return
  }

  // 确保滚动容器已初始化
  if (!scrollContainerRef.value) {
    attachScroll()
  }

  // 延迟执行，确保容器已设置
  nextTick(() => {
    handleLinkSelect(item)
  })
}

const getScrollTop = (container: HTMLElement | Window) =>
  container === window
    ? window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0
    : (container as HTMLElement).scrollTop

const getOffsetTop = (el: HTMLElement, container: HTMLElement | Window) => {
  const rect = el.getBoundingClientRect()
  if (container === window) {
    return rect.top + window.pageYOffset
  }
  const containerRect = (container as HTMLElement).getBoundingClientRect()
  return rect.top - containerRect.top + (container as HTMLElement).scrollTop
}

// 清除所有滚动定时器
const clearScrollTimers = () => {
  scrollTimers.value.forEach(timer => {
    clearTimeout(timer)
  })
  scrollTimers.value = []
}

// 清除用户滚动保护定时器
const clearUserScrollProtection = () => {
  if (userScrollProtectionTimer.value) {
    clearTimeout(userScrollProtectionTimer.value)
    userScrollProtectionTimer.value = null
  }
  isUserScrollProtected.value = false
}

// 确保元素可见（展开父级折叠面板等）
const ensureElementVisible = async (element: HTMLElement): Promise<void> => {
  let current: HTMLElement | null = element
  while (current && current !== document.body && current !== document.documentElement) {
    // 检查是否是 el-collapse-item__wrap（Element Plus 折叠面板的内容区域）
    if (current.classList.contains('el-collapse-item__wrap')) {
      const collapseItem = current.closest('.el-collapse-item')
      if (collapseItem) {
        const isActive = collapseItem.classList.contains('is-active')
        if (!isActive) {
          // 触发点击展开
          const header = collapseItem.querySelector('.el-collapse-item__header')
          if (header) {
            (header as HTMLElement).click()
            // 等待展开动画完成
            await new Promise<void>((resolve) => {
              setTimeout(() => resolve(), 250)
            })
            // 展开后继续检查父级
            break
          }
        }
      }
    }
    current = current.parentElement
  }
}

// 等待滚动完成
const waitForScrollComplete = (container: HTMLElement | Window, targetScrollTop: number, timeout: number = 1000): Promise<void> => {
  return new Promise((resolve) => {
    const startTime = Date.now()
    const checkComplete = () => {
      const currentScrollTop = container === window
        ? (window.pageYOffset || document.documentElement.scrollTop || 0)
        : (container as HTMLElement).scrollTop

      const deviation = Math.abs(currentScrollTop - targetScrollTop)
      const elapsed = Date.now() - startTime

      if (deviation < 2 || elapsed > timeout) {
        resolve()
      } else {
        requestAnimationFrame(checkComplete)
      }
    }
    requestAnimationFrame(checkComplete)
  })
}

const scrollToSection = async (href: string) => {
  // 取消之前的滚动操作
  clearScrollTimers()

  // 如果正在滚动，等待完成
  if (isScrolling.value) {
    await new Promise(resolve => setTimeout(resolve, 100))
  }

  const container = scrollContainerRef.value
  if (!container) return
  const targetWrapper = scrollTargetRef.value
  const baseContainer = targetWrapper || container
  const target = getAnchorElement(href)
  if (!target) return

  // 确保目标元素可见（展开折叠面板等）
  await ensureElementVisible(target)

  // 等待 DOM 更新
  await nextTick()

  // 重新获取目标元素，因为 DOM 可能已更新
  const finalTarget = getAnchorElement(href)
  if (!finalTarget) return

  isScrolling.value = true

  // 使用原生 scrollIntoView 方法，这是浏览器原生的锚点定位方式
  if (baseContainer === window || container === window) {
    // window 容器，使用 scrollIntoView
    const scrollOptions: ScrollIntoViewOptions = {
      behavior: props.scrollBehavior,
      block: 'start',
      inline: 'nearest'
    }

    finalTarget.scrollIntoView(scrollOptions)

    // 如果有 offset，需要额外调整位置
    if (props.offset > 0) {
      // 等待 scrollIntoView 完成
      if (props.scrollBehavior === 'smooth') {
        await new Promise(resolve => setTimeout(resolve, props.scrollDuration + 50))
      } else {
        await nextTick()
      }

      const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop || 0
      const targetTop = currentScrollTop - props.offset

      window.scrollTo({
        top: targetTop,
        behavior: 'auto'
      })

      // 等待调整完成
      await waitForScrollComplete(window, targetTop)

      isScrolling.value = false
      updateMarker()
    } else {
      // 没有 offset，等待滚动完成
      if (props.scrollBehavior === 'smooth') {
        await new Promise(resolve => setTimeout(resolve, props.scrollDuration + 100))
        const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop || 0
        await waitForScrollComplete(window, currentScrollTop)
        isScrolling.value = false
        updateMarker()
      } else {
        await nextTick()
        isScrolling.value = false
        updateMarker()
      }
    }
  } else {
    // 非 window 容器，使用更精确的位置计算
    const containerEl = baseContainer as HTMLElement

    // 等待一下，确保之前的滚动已完成
    await new Promise(resolve => setTimeout(resolve, 50))

    // 重新获取目标元素和计算位置，确保使用最新的 DOM 状态
    const updatedTarget = getAnchorElement(href)
    if (!updatedTarget) {
      isScrolling.value = false
      return
    }

    // 使用 getBoundingClientRect 计算位置，这是最可靠的方法
    // 等待一下确保 DOM 完全稳定
    await nextTick()

    const targetRect = updatedTarget.getBoundingClientRect()
    const containerRect = containerEl.getBoundingClientRect()

    // 计算元素相对于容器的位置
    const relativeTop = targetRect.top - containerRect.top
    // 计算目标滚动位置（考虑 offset）
    const targetScrollTop = containerEl.scrollTop + relativeTop - props.offset

    // 滚动到目标位置
    if (props.scrollBehavior === 'smooth') {
      containerEl.scrollTo({
        top: targetScrollTop,
        behavior: 'smooth'
      })

      // 等待滚动完成
      await new Promise(resolve => setTimeout(resolve, props.scrollDuration + 100))
      await waitForScrollComplete(containerEl, targetScrollTop)

      // 滚动完成后再次验证并微调
      const finalRect = updatedTarget.getBoundingClientRect()
      const finalContainerRect = containerEl.getBoundingClientRect()
      const finalRelativeTop = finalRect.top - finalContainerRect.top
      const finalTargetScrollTop = containerEl.scrollTop + finalRelativeTop - props.offset

      // 如果偏差较大，进行微调
      if (Math.abs(containerEl.scrollTop - finalTargetScrollTop) > 5) {
        containerEl.scrollTop = finalTargetScrollTop
      }

      isScrolling.value = false
      updateMarker()
    } else {
      containerEl.scrollTop = targetScrollTop

      // 立即验证并微调
      await nextTick()
      const finalRect = updatedTarget.getBoundingClientRect()
      const finalContainerRect = containerEl.getBoundingClientRect()
      const finalRelativeTop = finalRect.top - finalContainerRect.top
      const finalTargetScrollTop = containerEl.scrollTop + finalRelativeTop - props.offset

      // 如果偏差较大，进行微调
      if (Math.abs(containerEl.scrollTop - finalTargetScrollTop) > 5) {
        containerEl.scrollTop = finalTargetScrollTop
      }

      isScrolling.value = false
      updateMarker()
    }
  }
}

const attachScroll = () => {
  // 先移除旧的监听器（如果存在）
  if (scrollContainerRef.value) {
    const oldTarget = scrollContainerRef.value === window ? window : (scrollContainerRef.value as HTMLElement)
    oldTarget.removeEventListener('scroll', scrollListener)
  }

  // 延迟执行，确保DOM已渲染
  nextTick(() => {
    const container = getScrollContainer()
    const targetWrapper = getScrollTarget()
    scrollContainerRef.value = container
    scrollTargetRef.value = targetWrapper
    const target = container === window ? window : (container as HTMLElement)
    target.addEventListener('scroll', scrollListener, { passive: true })
  })
}

const detachScroll = () => {
  const container = scrollContainerRef.value
  if (!container) return
  const target = container === window ? window : (container as HTMLElement)
  target.removeEventListener('scroll', scrollListener)
}

const updateMarker = () => {
  if (!props.marker) return
  nextTick(() => {
    const list = listRef.value
    if (!list || !activeHref.value) return
    const selector = `[data-anchor-link="${escapeSelector(activeHref.value)}"]`
    const target = list.querySelector(selector) as HTMLElement | null
    if (!target) return
    if (props.direction === 'horizontal') {
      markerStyle.value = {
        width: `${target.offsetWidth}px`,
        transform: `translateX(${target.offsetLeft}px)`
      }
    } else {
      markerStyle.value = {
        height: `${target.offsetHeight}px`,
        transform: `translateY(${target.offsetTop}px)`
      }
    }
  })
}

const escapeSelector = (value: string) => {
  if (typeof value !== 'string') return ''
  if (typeof CSS !== 'undefined' && CSS.escape) {
    return CSS.escape(value)
  }
  return value.replace(/([ !"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, '\\$1')
}

const animateScroll = (
  target: HTMLElement | Window,
  top: number,
  duration: number,
  behavior: 'auto' | 'smooth'
) => {
  if (behavior === 'smooth') {
    if (target === window) {
      window.scrollTo({ top, behavior: 'smooth' })
    } else {
      ;(target as HTMLElement).scrollTo({ top, behavior: 'smooth' })
    }
    window.setTimeout(() => {
      isScrolling.value = false
      updateMarker()
    }, duration || 0)
    return
  }

  const start =
    target === window
      ? getScrollTop(window)
      : (target as HTMLElement).scrollTop
  const change = top - start
  const startTime = performance.now()
  const finalDuration = duration || 0

  const step = (timestamp: number) => {
    const progress = finalDuration
      ? Math.min((timestamp - startTime) / finalDuration, 1)
      : 1
    const value = start + change * progress
    if (target === window) {
      window.scrollTo(0, value)
    } else {
      ;(target as HTMLElement).scrollTop = value
    }
    if (progress < 1) {
      requestAnimationFrame(step)
    } else {
      isScrolling.value = false
      updateMarker()
    }
  }

  requestAnimationFrame(step)
}

onMounted(() => {
  // 延迟初始化，确保DOM和滚动容器都已准备好
  nextTick(() => {
    attachScroll()

    // 如果启用 hash 模式，监听 hashchange 事件
    if (props.useHash) {
      window.addEventListener('hashchange', handleHashChange)

      // 检查初始 hash
      const initialHash = window.location.hash.slice(1)
      if (initialHash) {
        const href = `#${initialHash}`
        const exists = flattenedItems.value.some(item => item.href === href)
        if (exists) {
          activeHref.value = href
          // 延迟滚动，确保 DOM 已渲染
          nextTick(() => {
            scrollToSection(href)
          })
        }
      }
    }

    // 再次延迟，确保滚动容器已完全初始化
    nextTick(() => {
      scrollListener()
      updateMarker()
    })
  })
})

onBeforeUnmount(() => {
  // 清理所有滚动定时器
  clearScrollTimers()
  // 清理用户滚动保护定时器
  clearUserScrollProtection()
  detachScroll()
  if (props.useHash) {
    window.removeEventListener('hashchange', handleHashChange)
  }
})

// 监听 items 变化，重新查找滚动容器
watch(
  () => props.items,
  () => {
    detachScroll()
    nextTick(() => {
      attachScroll()
      updateMarker()
    })
  },
  { deep: true }
)

// 监听自定义容器变化，重新初始化滚动容器
watch(
  () => [customTarget.value, customContainer.value],
  () => {
    detachScroll()
    nextTick(() => {
      attachScroll()
      updateMarker()
    })
  }
)

watch(
  () => props.marker,
  () => updateMarker()
)

watch(
  () => props.direction,
  () => updateMarker()
)

// 设置滚动容器的方法
const setContainer = (container: string | HTMLElement | { value?: HTMLElement }) => {
  customContainer.value = container
  detachScroll()
  nextTick(() => {
    attachScroll()
    updateMarker()
  })
}

// 设置滚动目标的方法
const setTarget = (target: string | HTMLElement | { value?: HTMLElement }) => {
  customTarget.value = target
  detachScroll()
  nextTick(() => {
    attachScroll()
    updateMarker()
  })
}

// 暴露方法供外部调用
__expose({
  scrollTo,
  setContainer,
  setTarget
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("nav", {
    ref_key: "anchorRef",
    ref: anchorRef,
    class: _normalizeClass(anchorClass.value),
    style: _normalizeStyle(anchorStyle.value)
  }, [
    _renderSlot(_ctx.$slots, "default", {
      items: __props.items,
      activeHref: activeHref.value,
      anchorLink: _unref(anchorLinkExpose),
      handleSelect: handleLinkSelect
    }, () => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          (markerVisible.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                class: _normalizeClass(["cm-anchor__marker", [`is-${direction.value}`]]),
                style: _normalizeStyle(markerStyle.value)
              }, [
                _renderSlot(_ctx.$slots, "marker", {
                  style: _normalizeStyle(markerStyle.value),
                  direction: direction.value
                }, () => [
                  _hoisted_3
                ])
              ], 6))
            : _createCommentVNode("", true),
          _createElementVNode("div", {
            ref_key: "listRef",
            ref: listRef,
            class: "cm-anchor__list"
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.items, (item) => {
              return (_openBlock(), _createBlock(AnchorLink, {
                key: item.href,
                item: item,
                indent: 0,
                "active-href": activeHref.value,
                "use-hash": props.useHash,
                onSelect: handleLinkSelect
              }, _createSlots({ _: 2 }, [
                (hasItemSlot.value)
                  ? {
                      name: "item",
                      fn: _withCtx((slotProps) => [
                        _renderSlot(_ctx.$slots, "item", _mergeProps({ ref_for: true }, slotProps))
                      ]),
                      key: "0"
                    }
                  : undefined,
                (hasTitleSlot.value)
                  ? {
                      name: "title",
                      fn: _withCtx((slotProps) => [
                        _renderSlot(_ctx.$slots, "title", _mergeProps({ ref_for: true }, slotProps))
                      ]),
                      key: "1"
                    }
                  : undefined
              ]), 1032, ["item", "active-href", "use-hash"]))
            }), 128))
          ], 512)
        ])
      ])
    ])
  ], 6))
}
}

})